import React, { useEffect, useReducer } from 'react';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import bundleType from '../../propTypes/bundle';
import { ContainerStyled, WrapperStyled } from './styles/ContentStyled';
import ContentScene from '../ContentScene/ContentScene';
import MarketplaceService from '../../../../services/Marketplace';
import HomeService from '../../../../services/Home';
import PaymentService from '../../../../services/Payment';
import Loading from '../../../General/Loading';
import MotionLoading from '../../../UI/MotionLoading/motionLoading';
import Error from '../../../General/Error';
import GhostError from '../../../../images/errores/fantasma.svg';
import Buying from '../Buying';
import Processing from '../Processing';
import useModal from '../../../../hooks/useModal';
import Modal from '../../../../shared/modal';
import { GoogleTagManager, rechargeUrl } from '../../../../helpers';
import useOtherLines from '../../../../hooks/useOtherLines';
import RetryIcon from '../../../../images/Payment-History/reintentar.svg';
import useDevice from '../../../../hooks/useDevice';
import { redirectInternalOrExternal } from '../../../../helpers/Redirect';

const Content = ({
  segment,
  closePanel,
  bundleData,
  isRecurring,
  setBundleBuySuccess,
  setIsPanelError,
}) => {
  // action types
  const ActionTypes = {
    setBalance: 'setBalance',
    setLoading: 'setLoading',
    setError: 'setError',
    setPurchaseVisible: 'setPurchaseVisible',
    setProcessingPurchase: 'setProcessingPurchase',
    setPurchaseData: 'setPurchaseData',
    setDisabledMethods: 'setDisabledMethods',
    setWompayCard: 'setPaymentMethod',
  };
  // initialState
  const initialState = {
    isLoading: false,
    isError: false,
    balance: 0,
    isPurchaseVisible: false,
    isProcessingPurchase: false,
    purchaseData: {
      method: '',
      data: {},
    },
    wompayCard: {
      maxAmount: 0,
      patActivated: false,
      id: '',
      name: '',
      lastname: '',
      lastFour: '',
      vendor: '',
    },
    disabledMethods: [],
  };

  // reducer
  const reducer = (state = initialState, action) => {
    switch (action.type) {
      case ActionTypes.setBalance: {
        const { balance } = action.payload;
        return {
          ...state,
          balance,
        };
      }
      case ActionTypes.setLoading: {
        const { isLoading } = action.payload;
        return {
          ...state,
          isLoading,
        };
      }
      case ActionTypes.setError: {
        const { isError } = action.payload;
        return {
          ...state,
          isError,
        };
      }
      case ActionTypes.setPurchaseVisible: {
        const { isPurchaseVisible } = action.payload;

        return {
          ...state,
          isPurchaseVisible,
        };
      }
      case ActionTypes.setProcessingPurchase: {
        const { isProcessingPurchase } = action.payload;
        return {
          ...state,
          isProcessingPurchase,
        };
      }
      case ActionTypes.setPurchaseData: {
        const { purchaseData } = action.payload;
        return {
          ...state,
          purchaseData,
        };
      }
      case ActionTypes.setDisabledMethods: {
        const { disabledMethods } = action.payload;
        return {
          ...state,
          disabledMethods,
        };
      }
      case ActionTypes.setWompayCard: {
        const { wompayCard } = action.payload;
        return {
          ...state,
          wompayCard,
        };
      }
      default:
        return state;
    }
  };

  // state
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isShowing, toggle } = useModal();
  const {
    beneficiaryNumber,
    credit,
    paymentType,
    setPaymentType,
    actionType,
    isOtherLines,
  } = useOtherLines();
  const { isSafari, isMobile } = useDevice();
  const rechargePortalUrl = rechargeUrl({
    linea: beneficiaryNumber,
    ver: isRecurring ? 'mwm-4' : 'mwm-2',
  });

  const handleIframeRender = () => {
    if (isSafari && isMobile) {
      redirectInternalOrExternal(rechargePortalUrl);
    } else toggle();
  };

  const { idTransaction } = credit;
  // action creators
  const setPurchaseVisible = isPurchaseVisible =>
    dispatch({
      type: ActionTypes.setPurchaseVisible,
      payload: { isPurchaseVisible },
    });

  const setBalance = balance =>
    dispatch({ type: ActionTypes.setBalance, payload: { balance } });

  const setLoading = isLoading =>
    dispatch({ type: ActionTypes.setLoading, payload: { isLoading } });

  const setError = isError =>
    dispatch({ type: ActionTypes.setError, payload: { isError } });

  const setProcessingPurchase = isProcessingPurchase =>
    dispatch({
      type: ActionTypes.setProcessingPurchase,
      payload: { isProcessingPurchase },
    });

  const setPurchaseData = purchaseData =>
    dispatch({
      type: ActionTypes.setPurchaseData,
      payload: { purchaseData },
    });

  const setDisabledMethods = disabledMethods =>
    dispatch({
      type: ActionTypes.setDisabledMethods,
      payload: { disabledMethods },
    });

  const setWompayCard = wompayCard =>
    dispatch({
      type: ActionTypes.setWompayCard,
      payload: { wompayCard },
    });

  const getInformationBundlesAndPayment = async () => {
    setLoading(true);

    Promise.allSettled([
      HomeService.getBalance(),
      PaymentService.getPaymentMethod(),
    ])
      .then(([responseBalance, responsePayment]) => {
        if (responseBalance.status === 'fulfilled') {
          const [{ balance: balanceDetails }] = responseBalance.value;
          const { total } = balanceDetails;
          setBalance(total);
        }

        if (responseBalance.status === 'rejected') {
          setError(true);
        }

        if (responsePayment.status === 'fulfilled') {
          setWompayCard(responsePayment.value[0]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // effects
  useEffect(() => {
    if (segment !== 'POSTPAID') {
      getInformationBundlesAndPayment();
    }
  }, []);

  // render
  const {
    isLoading,
    isError,
    balance,
    isPurchaseVisible,
    isProcessingPurchase,
    purchaseData,
    disabledMethods,
    wompayCard,
  } = state;

  const isOk = !isLoading && !isError;

  // methods
  const buyWebPayHandler = code => {
    const number = isOtherLines ? beneficiaryNumber : '';
    MarketplaceService.methodWebPaySend(code, segment, number)
      .then(res => {
        const [
          {
            channelId,
            paymentMethods: [{ url, encryptedCode }],
          },
        ] = res;
        const form = document.createElement('form');
        form.setAttribute('action', [url]);
        form.method = 'POST';
        form.setAttribute('name', 'purchaseForm');
        form.setAttribute('id', 'servicioPago');

        const value1 = document.createElement('input');
        value1.setAttribute('type', 'hidden');
        value1.setAttribute('name', 'IdCanal');
        value1.setAttribute('id', 'IdCanal');
        value1.setAttribute('value', channelId);
        form.appendChild(value1);

        const value2 = document.createElement('input');
        value2.setAttribute('type', 'hidden');
        value2.setAttribute('name', 'SolicitudCredito');
        value2.setAttribute('id', 'SolicitudCredito');
        value2.setAttribute('value', [encryptedCode]);
        form.appendChild(value2);

        document.body.appendChild(form);
        form.submit();
      })
      .catch(() => {
        setError(true);
        setIsPanelError(true);
      });
  };

  const buyWompayHandler = dataBundle => {
    const bodyRequest = {
      cardId: wompayCard.id,
      idBundle: dataBundle.code,
      descriptionBundle: dataBundle.description,
      amountBundle: parseInt(dataBundle.price, 10),
    };

    PaymentService.paymentOneShotBundle(bodyRequest)
      .then(([response]) => {
        navigate('/wompay/success', {
          state: { ...response, idCard: wompayCard.id },
        });
      })
      .catch(() => {
        setError(true);
        setIsPanelError(true);
      });
  };

  const buyAgainstInvoice = (dataBundle, numberUser) => {
    const { offerName, price, offerCode } = dataBundle;
    const bodyRequest = {
      action: actionType,
      amount: Number(price),
      codeTRX: idTransaction || ' ',
      paymentType,
      productCode: offerCode || ' ',
      productName: offerName || `Recarga ${price}`,
      serviceNumberBeneficiary: numberUser,
    };
    PaymentService.paymentAgainstInvoiceBeneficiary(bodyRequest)
      .then(() => {
        GoogleTagManager.pushToDataLayer({
          event: 'evento-interactivo',
          eventCategory: 'lineas_adicionales',
          eventAction: `pago_${paymentType}`,
          eventLabel: `BTN_PAGAR_${paymentType}`,
        });
        navigate('/mis-otras-lineas/success');
      })

      .catch(() => {
        setError(true);
        GoogleTagManager.pushToDataLayer({
          event: 'pagina-virtual',
          page: '/mis-otras-lineas/',
          pagename: `FACTURA_${paymentType}_PAGO_ERROR`,
        });
        setIsPanelError(true);
      });
  };

  const purchaseHandler = (method, dataBundle) => {
    let buyMethod = method;
    let buyData = dataBundle;
    if (buyMethod === undefined && buyData === undefined) {
      buyMethod = purchaseData.method;
      buyData = purchaseData.data;
    } else {
      setPurchaseData({ method: buyMethod, data: buyData });
    }
    if (buyMethod === 'RECARGAR') {
      return handleIframeRender();
    }
    if (buyMethod === 'CONTRAFACTURA-OTRAS-LINEAS') {
      return buyAgainstInvoice(buyData, beneficiaryNumber);
    }
    const { code } = buyData;
    setProcessingPurchase(true);
    setPurchaseVisible(true);
    if (buyMethod === 'WEBPAY') {
      return buyWebPayHandler(code);
    }

    if (buyMethod === 'WOMPAY') {
      return buyWompayHandler(buyData);
    }

    return MarketplaceService.buyBundle(buyMethod, code)
      .then(res => {
        setProcessingPurchase(false);
        return res;
      })
      .catch(err => {
        if (err.status === 402 && buyMethod === 'CONTRAFACTURA') {
          setDisabledMethods(['CONTRAFACTURA']);
          setPurchaseVisible(false);
          return err;
        }
        setError(true);
        setIsPanelError(true);
        return err;
      });
  };

  const getErrorButtonFunc = () => {
    if (isRecurring) {
      purchaseHandler();
    } else {
      setPaymentType('');
      closePanel();
    }
  };

  useEffect(() => {
    if (isError) {
      setBundleBuySuccess(false);
      setIsPanelError(true);
    }
  }, [isError]);

  useEffect(() => {
    GoogleTagManager.pushToDataLayer({
      event: 'pagina-virtual',
      page: isOtherLines
        ? `/mis-otras-lineas/${paymentType}s`
        : '/marketplace/categorias/bolsas/paso2',
      pagename: isOtherLines
        ? `FACTURA_${paymentType}_MEDIO_PAGO`
        : 'COMPRA_BOLSA_PASO_2',
    });
  }, []);

  if (isError && isOtherLines) {
    return (
      <ContainerStyled data-testid="service-error">
        <Error
          type="Ha ocurrido un error"
          text="No pudimos procesar tu solicitud.
Por favor reintenta nuevamente"
          button={{
            text: 'reintentar',
            click: getErrorButtonFunc,
            color: 'bubbleGum',
            outline: false,
            transparent: false,
            maxWidth: '100%',
          }}
          icon={RetryIcon}
        />
      </ContainerStyled>
    );
  }
  return (
    <>
      <Modal isShowing={isShowing} hide={toggle}>
        <iframe
          src={rechargePortalUrl}
          title="Recarga"
          sandbox="allow-same-origin allow-top-navigation allow-scripts allow-forms allow-downloads"
        />
      </Modal>
      <ContentScene buying />
      <WrapperStyled>
        {isLoading && (
          <ContainerStyled>
            <Loading />
          </ContainerStyled>
        )}
        <MotionLoading loading={isLoading}>
          {isOk && !isPurchaseVisible && (
            <Buying
              bundleData={bundleData}
              segment={segment}
              balance={balance}
              wompayCard={wompayCard}
              buyCallback={purchaseHandler}
              disabledMethods={disabledMethods}
              paymentType={paymentType}
              error={isError}
              fromOtherLines={isOtherLines}
            />
          )}
          {isOk && isPurchaseVisible && (
            <Processing
              loading={isProcessingPurchase}
              success={!isError}
              closeCallback={closePanel}
              retryCallback={purchaseHandler}
              isRecurring={isRecurring}
              setBundleBuySuccess={setBundleBuySuccess}
            />
          )}
          {!isLoading && isError && (
            <ContainerStyled data-testid="service-error">
              <Error
                type="Algo falló, no pudimos procesar tu solicitud"
                text="Por favor reintenta nuevamente"
                button={{
                  text: isRecurring ? 'reintentar' : 'volver',
                  click: () => {
                    getErrorButtonFunc();
                  },
                  color: 'bubbleGum',
                  outline: false,
                  transparent: false,
                }}
                icon={GhostError}
              />
            </ContainerStyled>
          )}
        </MotionLoading>
      </WrapperStyled>
    </>
  );
};

Content.defaultProps = {
  isRecurring: false,
  setBundleBuySuccess: () => null,
  segment: '',
  setIsPanelError: () => {},
};

Content.propTypes = {
  segment: PropTypes.string,
  closePanel: PropTypes.func.isRequired,
  bundleData: bundleType.isRequired,
  isRecurring: PropTypes.bool,
  setBundleBuySuccess: PropTypes.func,
  setIsPanelError: PropTypes.func,
};

export default Content;
